import {
  AGE_BAND_OPTIONS,
  HILLOS,
  LEARNER_GRADE_OPTIONS,
  SIDE_OPTIONS,
  isHilloNumber,
  procedureByKey,
  procedureLabel,
} from '@iculogbook/shared';
import type { LearnerGrade } from '@iculogbook/shared';

import {
  Button,
  ChipGroup,
  Disclosure,
  Field,
  NO_IDENTIFIABLE_INFORMATION,
  SegmentedControl,
  Select,
  TextArea,
  TextInput,
} from '../../components/index.ts';
import { CapabilityLevelField } from './CapabilityLevelField.tsx';
import { OccurredAtField } from './OccurredAtField.tsx';
import { MAX_ATTEMPTS, sideApplies, ultrasoundApplies, variantsFor } from './procedureForm.ts';
import type { ProcedureFormValues } from './procedureForm.ts';

export type FormErrors = Partial<Record<string, string>>;

type ProcedureFormProps = {
  values: ProcedureFormValues;
  onChange: (values: ProcedureFormValues) => void;
  /** Reopens the picker, keeping everything already entered. */
  onChangeProcedure: () => void;
  onSave: () => void;
  /** Saves, then opens a new form prefilled from what was just saved. */
  onSaveAndDuplicate: () => void;
  saving: boolean;
  errors: FormErrors;
  /** Editing a stored procedure rather than logging a new one. */
  editing?: boolean;
};

const ATTEMPT_OPTIONS = Array.from({ length: MAX_ATTEMPTS }, (_, index) => ({
  value: String(index + 1),
  label: String(index + 1),
}));

const SUCCESS_SEGMENTS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/**
 * The standalone procedure form: the fields, plus the chosen procedure and a
 * save button.
 *
 * The essentials sit at the top and fit on a phone screen without scrolling.
 * Everything else is behind a disclosure, closed by default — a trainee logging
 * an arterial line between two jobs should never see an attempts field.
 */
export function ProcedureForm({
  values,
  onChange,
  onChangeProcedure,
  onSave,
  onSaveAndDuplicate,
  saving,
  errors,
  editing = false,
}: ProcedureFormProps) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      {/* Kept to one line: the essentials below it have to fit on a phone. */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface pl-4">
        <span className="text-base font-strong text-text">
          {procedureLabel(values.procedureKey)}
        </span>
        <Button variant="ghost" onClick={onChangeProcedure}>
          Change
        </Button>
      </div>

      <ProcedureFields values={values} onChange={onChange} errors={errors} />

      <Button type="submit" variant="primary" block disabled={saving} className="h-14">
        {editing ? 'Save changes' : 'Save procedure'}
      </Button>

      <Button
        variant="secondary"
        block
        disabled={saving}
        onClick={onSaveAndDuplicate}
        className="h-14"
      >
        {editing ? 'Save changes and duplicate' : 'Save and duplicate'}
      </Button>
    </form>
  );
}

type ProcedureFieldsProps = {
  values: ProcedureFormValues;
  onChange: (values: ProcedureFormValues) => void;
  errors: FormErrors;
};

/**
 * Every field of a procedure except which procedure it is.
 *
 * Shared by the standalone form and the bottom sheet that adds a procedure to a
 * case, so a nested procedure offers exactly the same fields as a standalone
 * one — which is the whole point of copying the case's values onto it rather
 * than referencing them.
 */
export function ProcedureFields({ values, onChange, errors }: ProcedureFieldsProps) {
  const definition = procedureByKey(values.procedureKey);
  const variants = variantsFor(definition);

  function set<K extends keyof ProcedureFormValues>(key: K, value: ProcedureFormValues[K]): void {
    onChange({ ...values, [key]: value });
  }

  const detailCount = [
    values.attempts !== 1,
    !values.successful,
    values.complications.trim() !== '',
    values.indication.trim() !== '',
    values.note.trim() !== '',
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-4">
      <CapabilityLevelField
        value={values.capabilityLevel}
        onChange={(level) => set('capabilityLevel', level)}
        error={errors['capabilityLevel']}
      />

      {/*
        Set alongside capability level rather than lower down in "More
        detail": whether you were supervising someone else doing this specific
        procedure is exactly the same kind of fact as your own capability
        level on it, and a trainee deciding one should see the other. Who you
        supervised is not carried forward if this procedure is duplicated —
        that is a fact about this attempt, not the next one.
      */}
      <Disclosure
        title="Teaching or supervising"
        hint="Optional. Who you supervised doing this procedure."
        badge={
          values.teachingGrades.length === 0
            ? null
            : `${String(values.teachingGrades.length)} chosen`
        }
      >
        <ChipGroup
          label="Who you supervised"
          multiple
          options={LEARNER_GRADE_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.teachingGrades}
          onChange={(next) => set('teachingGrades', next as LearnerGrade[])}
        />
      </Disclosure>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Age band</span>
        <ChipGroup
          label="Age band"
          options={AGE_BAND_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
          value={values.ageBand}
          onChange={(next) => set('ageBand', next)}
        />
        {errors['ageBand'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['ageBand']}
          </p>
        )}
      </div>

      <OccurredAtField
        value={values.occurredAt}
        onChange={(next) => set('occurredAt', next)}
        error={errors['occurredAt']}
      />

      {variants.length === 0 ? null : (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">
            {definition?.variantLabel ?? 'Variant'}
          </span>
          <ChipGroup
            label={definition?.variantLabel ?? 'Variant'}
            options={variants.map((variant) => ({ value: variant, label: variant }))}
            value={values.variant}
            onChange={(next) => set('variant', next)}
          />
        </div>
      )}

      {!sideApplies(definition) ? null : (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">Side</span>
          <ChipGroup
            label="Side"
            options={SIDE_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
            value={values.side}
            onChange={(next) => set('side', next)}
          />
        </div>
      )}

      {!ultrasoundApplies(definition) ? null : (
        <div className="flex items-center justify-between gap-4 rounded-md border border-line bg-surface px-4 py-2">
          <span id="us-guided-label" className="text-sm font-medium text-text">
            Ultrasound guided
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={values.ultrasoundGuided}
            aria-labelledby="us-guided-label"
            onClick={() => set('ultrasoundGuided', !values.ultrasoundGuided)}
            className="grid h-touch w-16 place-items-center"
          >
            <span
              aria-hidden="true"
              className={
                values.ultrasoundGuided
                  ? 'flex h-7 w-12 items-center rounded-full bg-accent p-1'
                  : 'flex h-7 w-12 items-center rounded-full border border-line-strong bg-surface-sunken p-1'
              }
            >
              <span
                className={
                  values.ultrasoundGuided
                    ? 'ml-auto size-5 rounded-full bg-accent-contrast'
                    : 'size-5 rounded-full bg-line-strong'
                }
              />
            </span>
          </button>
        </div>
      )}

      <Disclosure
        title="More detail"
        hint="Attempts, complications, indication, note"
        badge={detailCount === 0 ? null : `${String(detailCount)} set`}
      >
        <div className="flex gap-4">
          <Field label="Attempts" className="w-28">
            <Select
              options={ATTEMPT_OPTIONS}
              value={String(values.attempts)}
              onChange={(event) => set('attempts', Number(event.target.value))}
            />
          </Field>

          <div className="flex flex-1 flex-col gap-1.5">
            <span className="text-sm font-medium text-text">Successful</span>
            <SegmentedControl
              label="Successful"
              segments={SUCCESS_SEGMENTS}
              value={values.successful ? 'yes' : 'no'}
              onChange={(next) => set('successful', next === 'yes')}
            />
          </div>
        </div>

        <Field
          label="Complications"
          help={NO_IDENTIFIABLE_INFORMATION}
          error={errors['complications']}
        >
          <TextArea
            value={values.complications}
            onChange={(event) => set('complications', event.target.value)}
            maxLength={300}
            rows={2}
          />
        </Field>

        <Field label="Indication" help={NO_IDENTIFIABLE_INFORMATION} error={errors['indication']}>
          <TextInput
            value={values.indication}
            onChange={(event) => set('indication', event.target.value)}
            maxLength={200}
          />
        </Field>

        <Field label="Note" help={NO_IDENTIFIABLE_INFORMATION} error={errors['note']}>
          <TextArea
            value={values.note}
            onChange={(event) => set('note', event.target.value)}
            maxLength={500}
            rows={3}
          />
        </Field>
      </Disclosure>

      <Disclosure
        title="Curriculum"
        hint="Optional HiLLO tagging. Nothing is stored until you tap."
        badge={values.hillos.length === 0 ? null : `${String(values.hillos.length)} tagged`}
      >
        <ChipGroup
          label="High Level Learning Outcomes"
          multiple
          options={HILLOS.map((hillo) => ({
            value: String(hillo.number),
            label: `${String(hillo.number)}. ${hillo.shortLabel}`,
          }))}
          value={values.hillos.map(String)}
          onChange={(next) => set('hillos', next.map(Number).filter(isHilloNumber))}
        />
      </Disclosure>
    </div>
  );
}
